import { LocalGameState, type PlayerStatus } from "./LocalGameState";
import type { GameClient } from "../controller/GameClient";

type Event = Record<string, unknown>;

function str(e: Event, key: string): string | undefined {
  const v = e[key];
  return typeof v === "string" ? v : undefined;
}

function num(e: Event, key: string): number | undefined {
  const v = e[key];
  return typeof v === "number" ? v : undefined;
}

function bool(e: Event, key: string): boolean | undefined {
  const v = e[key];
  return typeof v === "boolean" ? v : undefined;
}

function pos(e: Event, key: string): { x: number; y: number } | undefined {
  const p = e[key] as Record<string, unknown> | undefined;
  if (!p) return undefined;
  if (typeof p.x !== "number" || typeof p.y !== "number") return undefined;
  return { x: p.x, y: p.y };
}

export class EventProcessor {
  constructor(
    readonly state: LocalGameState,
    private gameClient: GameClient,
  ) {}

  setGameClient(client: GameClient): void {
    this.gameClient = client;
  }

  applyBatch(events: Event[]): void {
    this.state.pendingMovementPaths.clear();
    for (const event of events) {
      this.apply(event);
    }
    this.gameClient.onEventBatch(events, this.state);
  }

  private apply(event: Event): void {
    switch (event["type"]) {
      // ── Lifecycle ──────────────────────────────────────────────────────────
      case "GameStarted":             return this.applyGameStarted(event);
      case "CoinTossWinnerDetermined": return; // informational
      case "CoinTossResolved":         return; // informational
      case "HalfStarted":             this.state.activatedThisRound.clear(); return;
      case "DriveStarted":            return this.applyDriveStarted(event);
      case "DownStarted":             return this.applyDownStarted(event);
      case "DownEnded":               return this.applyDownEnded();
      case "DriveEnded":              return; // informational
      case "HalfEnded":               return; // informational
      case "GameEnded":               return this.applyGameEnded(event);

      // ── Formation ─────────────────────────────────────────────────────────
      case "FormationsRevealed":      return this.applyFormationsRevealed(event);

      // ── Activation ────────────────────────────────────────────────────────
      case "ActivationRoundStarted":  this.state.activatedThisRound.clear(); return;
      case "PlayerActivated":         return this.applyPlayerActivated(event);
      case "ActivationRoundEnded":    return this.applyActivationRoundEnded(event);

      // ── Movement ──────────────────────────────────────────────────────────
      case "PlayerMoved":             return this.applyPlayerMoved(event);
      case "StandUpAttempted":        return this.applyStandUpAttempted(event);

      // ── Tackle ────────────────────────────────────────────────────────────
      case "TackleAttempted":         return; // informational
      case "TackleResolved":          return this.applyTackleResolved(event);
      case "TackleActivationEnded":   return; // informational

      // ── Push ──────────────────────────────────────────────────────────────
      case "PushInitiated":           return; // informational
      case "PushAttempted":           return; // informational
      case "PushRollResolved":        return; // informational
      case "PlayerPushed":            return this.applyPlayerPushed(event);
      case "ChainPushTriggered":      return; // informational
      case "BallCarrierPushed":       return this.applyBallCarrierPushed(event);
      case "BallDroppedFromPush":     return this.applyBallDroppedFromPush(event);
      case "BallCarrierLockedUp":     return this.applyBallCarrierLockedUp(event);
      case "BallDroppedFromLockUp":   return this.applyBallDroppedFromLockUp(event);
      case "LockUpInitiated":         return this.applyLockUpInitiated(event);
      case "LockUpBroken":            return this.applyLockUpBroken(event);
      case "LockUpGroupFreed":        return this.applyLockUpGroupFreed(event);

      // ── Injury ────────────────────────────────────────────────────────────
      case "InjuryRollTriggered":     return; // informational
      case "InjuryRolled":            return; // informational
      case "InjuryApplied":           return this.applyInjuryApplied(event);
      case "StunCleared":             return this.applyStunCleared(event);
      case "RecoveryRolled":          return this.applyRecoveryRolled(event);

      // ── Throw ─────────────────────────────────────────────────────────────
      case "HandoffAttempted":              this.state.ballCarrierId = null; this.applyThrowAttempted(event); return;
      case "PassAttempted":                 this.state.ballCarrierId = null; this.applyThrowAttempted(event); return;
      case "PassScatterDetermined":         return; // informational
      case "HandoffCatchRolled":            return this.applyHandoffCatchRolled(event);

      // ── Scatter ───────────────────────────────────────────────────────────
      case "ScatterStarted":          return this.applyScatterStarted(event);
      case "BallLanded":              return this.applyBallLanded(event);
      case "CatchAttempted":          return this.applyCatchAttempted(event);
      case "ScatterChainTerminated":  return; // final position captured by BallLanded

      // ── Bomb ──────────────────────────────────────────────────────────────
      case "BombTrackerIncremented":
        this.state.bombTracker = num(event, "newValue") ?? this.state.bombTracker;
        return;
      case "BombExplosionTriggered":  return; // informational; injury via InjuryApplied
      case "BombExplosionResolved":   return; // informational; injury via InjuryApplied
      case "BombExplodedUnattended":  return; // informational

      // ── Scoring ───────────────────────────────────────────────────────────
      case "TouchdownScored":         return this.applyTouchdownScored(event);
      case "PossessionChanged":       return this.applyPossessionChanged(event);
      case "PlayerKnelt": {
        if (this.state.ballCarrierId) {
          this.state.playerStatus.set(this.state.ballCarrierId, "PRONE");
        }
        return;
      }
      case "BallCarrierWentProne":    return this.applyBallCarrierWentProne(event);
      case "BallDroppedFromProne":    return this.applyBallDroppedFromProne(event);
      case "BallPlaced":              return this.applyBallPlaced(event);

      // ── Drive Progression ─────────────────────────────────────────────────
      case "DownCounterIncremented":
        this.state.downNumber = num(event, "newDownNumber") ?? this.state.downNumber;
        return;
      case "FirstDownAchieved":       return this.applyFirstDownAchieved(event);
    }
  }

  private applyGameStarted(event: Event): void {
    this.state.homeTeamId = str(event, "homeTeamId") ?? null;
    this.state.awayTeamId = str(event, "awayTeamId") ?? null;
    const config = event["config"] as Record<string, unknown> | undefined;
    if (!config) return;
    const field = config["field"] as Record<string, unknown> | undefined;
    if (!field) return;
    const oob = (field["outOfBoundsDepth"] as number | undefined) ?? 1;
    const pzw = (field["playZoneWidth"] as number | undefined) ?? 7;
    const ezd = (field["endZoneDepth"] as number | undefined) ?? 2;
    const pzl = (field["playZoneLength"] as number | undefined) ?? 20;
    this.state.fieldWidth = oob * 2 + pzw;
    this.state.fieldHeight = ezd * 2 + pzl;
    this.state.endZoneDepth = ezd;
    this.state.outOfBoundsDepth = oob;
    const game = config["game"] as Record<string, unknown> | undefined;
    this.state.downMax = (game?.["downsPerDrive"] as number | undefined) ?? 3;
    this.state.playersPerDown = (game?.["playersPerDown"] as number | undefined) ?? 5;
    this.state.maxPassRange = (game?.["maxPassRange"] as number | undefined) ?? 5;

    // Player IDs, names, and stats all arrive via GAME_STATE_SNAPSHOT (sent both before and after this event).
    this.gameClient.onGameStarted?.(this.state);
  }

  private applyDriveStarted(event: Event): void {
    const bp = pos(event, "ballPosition");
    this.state.lineOfScrimmage = bp?.y ?? null;
    this.state.firstDownLine = num(event, "firstDownLine") ?? null;
    this.state.offenseTeamId = str(event, "offenseTeamId") ?? null;
    this.state.downNumber = 1;
    this.state.lockedGroups = [];
    this.state.lockedPairs = [];
    this.state.activatedThisRound.clear();
    this.state.ballCarrierId = null;
    this.state.playerPositions.clear();
    // Retain persistent injury statuses (KNOCKED_OUT, INCAPACITATED); clear transient ones
    for (const [id, status] of this.state.playerStatus) {
      if (status !== "KNOCKED_OUT" && status !== "INCAPACITATED") {
        this.state.playerStatus.delete(id);
      }
    }
    // playerInjuries persists across drives — only cleared by RecoveryRolled
    if (bp) this.state.ballPosition = [bp.x, bp.y];
  }

  private applyDownStarted(event: Event): void {
    this.state.downNumber = num(event, "downNumber") ?? this.state.downNumber;
    this.state.bombTracker = num(event, "bombTracker") ?? this.state.bombTracker;
    const bp = pos(event, "ballPosition");
    if (bp) this.state.ballPosition = [bp.x, bp.y];
  }

  private applyFormationsRevealed(event: Event): void {
    this.state.lockedGroups = [];
    this.state.activatedThisRound.clear();

    const homePlacements = event["homePlacements"] as Record<string, Record<string, unknown>> | undefined;
    const awayPlacements = event["awayPlacements"] as Record<string, Record<string, unknown>> | undefined;
    this.applyPlacements(homePlacements, true);
    this.applyPlacements(awayPlacements, false);

    this.state.ballCarrierId = str(event, "ballCarrierId") ?? null;
    const bp = pos(event, "ballPosition");
    if (bp) this.state.ballPosition = [bp.x, bp.y];
  }

  private applyPlacements(
    placementsNode: Record<string, Record<string, unknown>> | undefined,
    isHome: boolean,
  ): void {
    if (!placementsNode) return;
    const prefix = isHome ? "H" : "A";
    const orderedIds = isHome ? this.state.homePlayerIds : this.state.awayPlayerIds;
    const teamId = isHome ? (this.state.homeTeamId ?? "") : (this.state.awayTeamId ?? "");

    for (const [id, posNode] of Object.entries(placementsNode)) {
      const x = typeof posNode.x === "number" ? posNode.x : undefined;
      const y = typeof posNode.y === "number" ? posNode.y : undefined;
      if (x === undefined || y === undefined) continue;

      this.state.playerPositions.set(id, [x, y]);
      this.state.playerStatus.set(id, "NORMAL");

      if (!this.state.playerIdToLabel.has(id)) {
        const idx = orderedIds.indexOf(id);
        const label = idx >= 0
          ? `${prefix}${idx + 1}`
          : `${prefix}${[...this.state.playerIdToLabel.values()].filter((l) => l.startsWith(prefix)).length + 1}`;
        this.state.playerIdToLabel.set(id, label);
        this.state.labelToPlayerId.set(label, id);

        const existing = this.state.players.get(id);
        if (existing) {
          this.state.players.set(id, { ...existing, label, teamId });
        } else {
          // Snapshot hasn't arrived yet — skeleton filled in when snapshot comes.
          this.state.players.set(id, { playerId: id, label, teamId, role: "", mov: 0, str: 0, dex: 0, skills: [] });
        }
        if (!orderedIds.includes(id)) orderedIds.push(id);
      }
    }
  }

  private applyPlayerMoved(event: Event): void {
    const id = str(event, "playerId");
    if (!id) return;
    const to = pos(event, "to");
    if (!to) return;

    // Collect movement path for animation
    const currentPos = this.state.playerPositions.get(id);
    if (currentPos) {
      const existing = this.state.pendingMovementPaths.get(id);
      if (!existing) {
        this.state.pendingMovementPaths.set(id, [[currentPos[0], currentPos[1]], [to.x, to.y]]);
      } else {
        existing.push([to.x, to.y]);
      }
    }

    this.state.playerPositions.set(id, [to.x, to.y]);
    if (this.state.ballCarrierId === id) {
      this.state.ballPosition = [to.x, to.y];
    }

    // Track that this player has acted in their activation
    this.state.hasActedThisActivation.add(id);

    const remaining = num(event, "movementPointsRemaining");
    if (remaining !== undefined) {
      this.state.movementRemaining.set(id, remaining);
    }
  }

  private applyPlayerPushed(event: Event): void {
    const id = str(event, "playerId");
    if (!id) return;
    const to = pos(event, "to");
    if (!to) return;
    this.state.playerPositions.set(id, [to.x, to.y]);
    if (this.state.ballCarrierId === id) {
      this.state.ballPosition = [to.x, to.y];
    }
  }

  private applyStandUpAttempted(event: Event): void {
    const success = bool(event, "success");
    const id = str(event, "playerId");
    if (success && id) {
      this.state.playerStatus.set(id, "NORMAL");

      // Track that this player has acted in their activation
      this.state.hasActedThisActivation.add(id);

      // Deduct movement points for standing up (similar to PlayerMoved)
      const remaining = num(event, "movementPointsRemaining");
      if (remaining !== undefined) {
        this.state.movementRemaining.set(id, remaining);
      }
    }
  }

  private applyInjuryApplied(event: Event): void {
    const id = str(event, "playerId");
    const effect = str(event, "effect");
    if (!id || !effect) return;
    const statusMap: Record<string, PlayerStatus> = {
      DOWNED: "PRONE",
      STUNNED: "STUNNED",
      KNOCKED_OUT: "KNOCKED_OUT",
      CONCUSSED: "INCAPACITATED",
      BAD_SPRAIN: "INCAPACITATED",
      MISS_NEXT_GAME: "INCAPACITATED",
      BROKEN_LEG: "INCAPACITATED",
      BROKEN_ARM: "INCAPACITATED",
      TORN_ACL: "INCAPACITATED",
      DEAD: "INCAPACITATED",
    };
    this.state.playerStatus.set(id, statusMap[effect] ?? "PRONE");
    this.state.playerInjuries.set(id, effect);

    const statChange = event["permanentStatChange"] as { stat?: unknown; delta?: unknown } | null | undefined;
    if (statChange) {
      const stat = statChange.stat;
      const delta = statChange.delta;
      if ((stat === "MOV" || stat === "STR" || stat === "DEX") && typeof delta === "number") {
        const existing = this.state.players.get(id);
        if (existing) {
          this.state.players.set(id, {
            ...existing,
            mov: stat === "MOV" ? existing.mov + delta : existing.mov,
            str: stat === "STR" ? existing.str + delta : existing.str,
            dex: stat === "DEX" ? existing.dex + delta : existing.dex,
          });
        }
      }
    }
  }

  private applyStunCleared(event: Event): void {
    const id = str(event, "playerId");
    if (!id) return;
    if (this.state.getPlayerStatus(id) === "STUNNED") {
      this.state.playerStatus.set(id, "PRONE");
    }
  }

  private applyRecoveryRolled(event: Event): void {
    const recovered = bool(event, "recovered");
    const id = str(event, "playerId");
    if (recovered && id) {
      this.state.playerStatus.set(id, "PRONE");
      this.state.playerInjuries.delete(id);
    }
  }

  private applyLockUpInitiated(event: Event): void {
    const a = str(event, "attackerId");
    const d = str(event, "defenderId");
    if (!a || !d) return;
    this.state.hasActedThisActivation.add(a);
    this.state.lockedPairs.push([a, d]);
    const existingIdx = this.state.lockedGroups.findIndex((g) => g.has(a) || g.has(d));
    if (existingIdx >= 0) {
      const updated = new Set(this.state.lockedGroups[existingIdx]);
      updated.add(a);
      updated.add(d);
      this.state.lockedGroups[existingIdx] = updated;
    } else {
      this.state.lockedGroups.push(new Set([a, d]));
    }
  }

  private applyLockUpBroken(event: Event): void {
    const id = str(event, "playerId");
    if (!id) return;
    this.state.lockedGroups = this.state.lockedGroups
      .map((g) => { const n = new Set(g); n.delete(id); return n; })
      .filter((g) => g.size > 1);
    this.state.lockedPairs = this.state.lockedPairs.filter(([a, d]) => a !== id && d !== id);
  }

  private applyLockUpGroupFreed(event: Event): void {
    const freedIds = event["freedPlayerIds"] as string[] | undefined;
    if (!freedIds) return;
    const freed = new Set(freedIds);
    this.state.lockedGroups = this.state.lockedGroups.filter((g) => ![...g].some((id) => freed.has(id)));
    this.state.lockedPairs = this.state.lockedPairs.filter(([a, d]) => !freed.has(a) && !freed.has(d));
  }

  private applyTackleResolved(event: Event): void {
    const id = str(event, "attackerId");
    if (id) {
      this.state.hasAttacked.add(id);
      this.state.hasActedThisActivation.add(id);
    }
  }

  private applyPlayerActivated(event: Event): void {
    const id = str(event, "playerId");
    if (id) this.state.activatedThisRound.add(id);
  }

  private applyActivationRoundEnded(event: Event): void {
    this.state.gameClock = num(event, "newGameClock") ?? this.state.gameClock;
    this.state.bombTracker = num(event, "newBombTracker") ?? this.state.bombTracker;
    this.state.activatedThisRound.clear();
    this.state.movementRemaining.clear();
    this.state.hasAttacked.clear();
    this.state.hasActedThisActivation.clear();
  }

  private applyPossessionChanged(_event: Event): void {
    this.state.ballCarrierId = null;
  }

  private applyBallPlaced(event: Event): void {
    const p = pos(event, "position");
    if (!p) return;
    this.state.ballPosition = [p.x, p.y];
    this.state.lineOfScrimmage = p.y;
  }

  private applyScatterStarted(event: Event): void {
    const to = pos(event, "toPosition");
    if (to) this.state.ballPosition = [to.x, to.y];
  }

  private applyBallLanded(event: Event): void {
    const p = pos(event, "position");
    if (p) this.state.ballPosition = [p.x, p.y];
  }

  private applyThrowAttempted(event: Event): void {
    const id = str(event, "throwerId");
    if (id) this.state.hasActedThisActivation.add(id);
  }

  private applyCatchAttempted(event: Event): void {
    const caught = bool(event, "caught");
    const id = str(event, "playerId");
    if (!id) return;
    if (caught) {
      const p = pos(event, "position");
      if (!p) return;
      this.state.ballCarrierId = id;
      this.state.ballPosition = [p.x, p.y];
    } else {
      this.state.ballCarrierId = null;
    }
  }

  private applyHandoffCatchRolled(event: Event): void {
    const caught = bool(event, "caught");
    const id = str(event, "catcherId");
    if (!id) return;
    if (caught) {
      this.state.ballCarrierId = id;
      this.state.ballPosition = this.state.playerPositions.get(id) ?? this.state.ballPosition;
    } else {
      this.state.ballCarrierId = null;
    }
  }

  private applyBallCarrierPushed(event: Event): void {
    const held = bool(event, "held");
    if (!held) this.state.ballCarrierId = null;
  }

  private applyBallDroppedFromPush(event: Event): void {
    const p = pos(event, "dropPosition");
    if (!p) return;
    this.state.ballCarrierId = null;
    this.state.ballPosition = [p.x, p.y];
  }

  private applyBallCarrierWentProne(event: Event): void {
    const held = bool(event, "held");
    if (!held) this.state.ballCarrierId = null;
  }

  private applyBallDroppedFromProne(event: Event): void {
    const p = pos(event, "dropPosition");
    if (!p) return;
    this.state.ballCarrierId = null;
    this.state.ballPosition = [p.x, p.y];
  }

  private applyBallCarrierLockedUp(event: Event): void {
    const held = bool(event, "held");
    if (!held) this.state.ballCarrierId = null;
  }

  private applyBallDroppedFromLockUp(event: Event): void {
    const p = pos(event, "dropPosition");
    if (!p) return;
    this.state.ballCarrierId = null;
    this.state.ballPosition = [p.x, p.y];
  }

  private applyDownEnded(): void {
    this.state.playerPositions.clear();
    // Retain persistent injury statuses (KNOCKED_OUT, INCAPACITATED); clear transient ones
    for (const [id, status] of this.state.playerStatus) {
      if (status !== "KNOCKED_OUT" && status !== "INCAPACITATED") {
        this.state.playerStatus.delete(id);
      }
    }
    this.state.movementRemaining.clear();
    this.state.lockedGroups = [];
    this.state.lockedPairs = [];
    this.state.ballCarrierId = null;
    this.state.ballPosition = null;
    this.state.activatedThisRound.clear();
    this.state.hasAttacked.clear();
  }

  private applyTouchdownScored(event: Event): void {
    const teamId = str(event, "scoringTeamId");
    const points = num(event, "points") ?? 1;
    if (teamId === this.state.homeTeamId) {
      this.state.homeScore += points;
    } else {
      this.state.awayScore += points;
    }
    this.state.ballCarrierId = null;
  }

  private applyFirstDownAchieved(event: Event): void {
    const bp = event["newBallPosition"] as Record<string, unknown> | undefined;
    const x = typeof bp?.x === "number" ? bp.x : undefined;
    const y = typeof bp?.y === "number" ? bp.y : undefined;
    if (y !== undefined) this.state.lineOfScrimmage = y;
    if (x !== undefined && y !== undefined) this.state.ballPosition = [x, y];
    this.state.firstDownLine = num(event, "newFirstDownLine") ?? this.state.firstDownLine;
    this.state.downNumber = 1;
  }

  private applyGameEnded(event: Event): void {
    this.state.homeScore = num(event, "homeScore") ?? this.state.homeScore;
    this.state.awayScore = num(event, "awayScore") ?? this.state.awayScore;
    this.state.phase = "GAME_OVER";
  }
}
